import { useState } from "react";
import { paymentRecord, refundRecord } from "@shared/schemas/payments";
import { formatCents } from "@shared/money";
import { damageCollected, refundableAmount } from "@shared/paymentAmounts";
import { validatePayment } from "@shared/validation/finance";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { useFormValidation } from "@web/lib/formValidation";
import { fmtDate, todayIn } from "@web/lib/format";
import { api, type BookingDetail } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, MoneyField, Picker, Stack, TextArea, TextField, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

const METHODS = [
  { value: "check", label: "Check" },
  { value: "cash", label: "Cash" },
  { value: "zelle", label: "Zelle" },
  { value: "other", label: "Other" },
];
const KINDS = [
  { value: "deposit", label: "Deposit" },
  { value: "balance", label: "Balance" },
  { value: "damage_deposit", label: "Damage deposit" },
  { value: "extra", label: "Extra" },
  { value: "custom", label: "Custom" },
];

/** Record a manual payment (cash / check / Zelle / other) → POST /payments. */
export function RecordPaymentSheet({ b, open, onClose }: { b: BookingDetail; open: boolean; onClose: () => void }) {
  const me = useMe();
  const toast = useToast();
  const depositOwed = Math.max(0, b.depositAmount - b.paidTotal);
  const [kind, setKind] = useState(depositOwed > 0 ? "deposit" : "balance");
  const [method, setMethod] = useState("check");
  const [amount, setAmount] = useState<number | null>(depositOwed > 0 ? depositOwed : Math.max(0, b.balanceDue) || null);
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(todayIn(me.organization.timezone));
  const [notes, setNotes] = useState("");
  const body = { kind, method, amount, reference: reference || null, receivedAt: date || undefined, notes: notes || null };
  const [initialDraft] = useState(() => JSON.stringify(body));
  const parsed = parseInput(paymentRecord, body, { type: "payment" }, "payment.record");
  const validation = useFormValidation(evaluation([...parsed.issues, ...validatePayment({ ...body, bookingId: b.id }, { action: "payment.record", booking: b }).issues], false));
  const { busy, field } = validation;

  function pickKind(k: string) {
    setKind(k);
    if (k === "deposit") setAmount(depositOwed || null);
    else if (k === "balance") setAmount(Math.max(0, b.balanceDue) || null);
    else if (k === "damage_deposit") setAmount(Math.max(0, b.damageAmount - damageCollected(b.payments)) || null);
  }

  async function save() {
    if (!parsed.success || !validation.begin("payment.record")) return;
    try {
      await api.recordPayment(b.id, parsed.data);
      toast.success(`Recorded ${formatCents(parsed.data.amount)}`);
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title="Record payment"
      footer={
        <Button loading={busy} onClick={save}>
          Record payment
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!busy && JSON.stringify(body) !== initialDraft} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <Picker label="For" value={kind} onChange={v => validation.edit(() => pickKind(v))} options={KINDS} {...field("kind")} />
        <Picker label="Method" value={method} onChange={v => validation.edit(() => setMethod(v))} options={METHODS} {...field("method")} />
        <MoneyField label="Amount" value={amount} onChange={v => validation.edit(() => setAmount(v))} hint={b.balanceDue > 0 ? `${formatCents(b.balanceDue)} still due` : "Paid in full"} {...field("amount")} />
        <TextField label="Reference" placeholder="Check no., Zelle memo…" value={reference} onChange={(e) => validation.edit(() => setReference(e.target.value))} {...field("reference")} />
        <TextField label="Received on" type="date" value={date} onChange={(e) => validation.edit(() => setDate(e.target.value))} {...field("receivedAt")} />
        <TextArea label="Notes" value={notes} onChange={(e) => validation.edit(() => setNotes(e.target.value))} {...field("notes")} />
      </Stack>
    </BottomSheet>
  );
}

/** Admins: record a refund made outside the app (check, Zelle, cash) → POST /refunds. */
export function RecordRefundSheet({ b, open, onClose }: { b: BookingDetail; open: boolean; onClose: () => void }) {
  const me = useMe();
  const toast = useToast();
  const originals = b.payments.filter((p) => refundableAmount(p, b.payments) > 0);
  const [of, setOf] = useState(originals.length === 1 ? originals[0].id : "");
  const [method, setMethod] = useState("check");
  const [amount, setAmount] = useState<number | null>(originals.length === 1 ? refundableAmount(originals[0], b.payments) : null);
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(todayIn(me.organization.timezone));
  const [notes, setNotes] = useState("");
  const original = b.payments.find(p => p.id === of);
  const body = { amount, method, reference: reference || null, refundOfPaymentId: of, receivedAt: date || undefined, notes: notes || null };
  const [initialDraft] = useState(() => JSON.stringify(body));
  const parsed = parseInput(refundRecord, body, { type: "payment" }, "payment.refund");
  const validation = useFormValidation(evaluation([...parsed.issues, ...validatePayment({ ...body, bookingId: b.id, kind: "refund" }, { action: "payment.refund", booking: b, original, remainingRefundCents: original ? refundableAmount(original, b.payments) : undefined }).issues], false));
  const { busy, field } = validation;

  async function save() {
    if (!parsed.success || !validation.begin("payment.refund")) return;
    try {
      await api.recordRefund(b.id, parsed.data);
      toast.success(`Refund of ${formatCents(parsed.data.amount)} recorded`);
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title="Record refund"
      footer={
        <Button variant="destructive" loading={busy} onClick={save}>
          Record refund
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!busy && JSON.stringify(body) !== initialDraft} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <Picker
          label="Refund of"
          placeholder="Choose original payment"
          value={of}
          onChange={(v) => validation.edit(() => {
            setOf(v);
            const p = originals.find((x) => x.id === v);
            if (p) setAmount(refundableAmount(p, b.payments));
          })}
          options={originals.map((p) => ({ value: p.id, label: `${formatCents(refundableAmount(p, b.payments))} refundable · ${p.kind.replace("_", " ")} · ${p.legacyRef ? "Receipt date unknown" : fmtDate(p.receivedAt.slice(0, 10))}` }))}
          {...field("refundOfPaymentId")}
        />
        <MoneyField label="Amount" value={amount} onChange={v => validation.edit(() => setAmount(v))} {...field("amount")} />
        <Picker label="Method" value={method} onChange={v => validation.edit(() => setMethod(v))} options={METHODS} {...field("method")} />
        <TextField label="Reference" value={reference} onChange={(e) => validation.edit(() => setReference(e.target.value))} {...field("reference")} />
        <TextField label="Refunded on" type="date" value={date} onChange={(e) => validation.edit(() => setDate(e.target.value))} {...field("receivedAt")} />
        <TextArea label="Notes" value={notes} onChange={(e) => validation.edit(() => setNotes(e.target.value))} {...field("notes")} />
      </Stack>
    </BottomSheet>
  );
}
